import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { enableNotifications, listenNotifications } from "./firebaseMessaging";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";

import TopNav from "./components/TopNav";
import BottomNav from "./components/BottomNav";

import ReferralTracker from "./components/ReferralTracker";

import Home from "./pages/Home";
import Posts from "./pages/posts";
import Login from "./pages/Login";
import UserProfile from "./pages/UserProfile";
import UserSettings from "./pages/UserSettings";
import UserPosts from "./pages/UserPosts";
import PostViewer from "./pages/PostViewer";
import Videos from "./pages/Videos";
import AddVideo from "./pages/AddVideo";
import MyVideos from "./pages/MyVideos";
import Shops from "./pages/Shops";
import UserShops from "./pages/UserShops";
import Loans from "./pages/Loans";
import Join from "./pages/join";
import Volunteers from "./pages/Volunteers";
import BusinessOTPVerification from "./pages/BusinessOTPVerification";

import Admin from "./pages/Admin";

import Autos from "./pages/Autos";
import JoinAuto from "./pages/JoinAuto";
import AutoDashboard from "./pages/AutoDashboard";
import RideRequests from "./pages/RideRequests";
import MyRides from "./pages/MyRides";

import JoinDeliveryPerson from "./pages/JoinDeliveryPerson";
import DeliveryDashboard from "./pages/DeliveryDashboard";

import ShopProducts from "./pages/ShopProducts";

import Invite from "./pages/Invite";
import ShopDetail from "./pages/ShopDetail";
import ShopOrders from "./pages/ShopOrders";

import ManageProducts from "./pages/ManageProducts";
import AddProduct from "./pages/AddProduct";
import EditProduct from "./pages/EditProduct";

import Products from "./pages/Products";
import AllProducts from "./pages/AllProducts";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/checkout";
import Success from "./pages/success";
import MyOrders from "./pages/MyOrders";
import More from "./pages/More";
import Creators from "./pages/creators";
import InfoExchange from "./pages/InfoExchange";
import LocalUpdates from "./pages/LocalUpdates";
import Play from "./pages/play";
import AIShop from "./pages/AIShop";
import FloatingAI from "./components/FloatingAI";
import Bills from "./pages/bills";
import Wallet from "./pages/wallet";
import Deals from "./pages/deals";
import Jobs from "./pages/Jobs";

import BookRide from "./pages/BookRide";
import ForYou from "./pages/ForYou";

import Payments from "./pages/Payments";
import EPOS from "./pages/ePOS";


/* =========================================================
   PrivateRoute
========================================================= */

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ textAlign: "center", paddingTop: "100px" }}>
        <h3>Loading...</h3>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
}


/* =========================================================
   PWA INSTALL SYSTEM
   Supports:
   - Android Chrome
   - Android Edge
   - Samsung Internet
   - Android Opera
   - Vivo browsers based on Chromium
   - OPPO browsers based on Chromium
   - iPhone Safari
   - iPad Safari
   - iPhone Chrome
   - iPad Chrome
   - Desktop Chrome / Edge
========================================================= */

function usePWAInstallPrompt() {
  useEffect(() => {
    let deferredPrompt = null;
    let popupElement = null;

    const STORAGE_KEY = "eyal_install_popup_dismissed";
    const DISMISS_DAYS = 3;

    /* -------------------------------------------------------
       Detect standalone / already installed
    ------------------------------------------------------- */

    const isStandalone = () => {
      return (
        window.matchMedia &&
        window.matchMedia("(display-mode: standalone)").matches
      ) ||
      window.navigator.standalone === true ||
      window.matchMedia("(display-mode: fullscreen)").matches ||
      window.matchMedia("(display-mode: minimal-ui)").matches;
    };


    /* -------------------------------------------------------
       Detect iOS
    ------------------------------------------------------- */

    const isIOS = () => {
      return (
        /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" &&
          navigator.maxTouchPoints > 1)
      );
    };


    /* -------------------------------------------------------
       Detect Android
    ------------------------------------------------------- */

    const isAndroid = () => {
      return /Android/i.test(navigator.userAgent);
    };


    /* -------------------------------------------------------
       Detect browser
    ------------------------------------------------------- */

    const getBrowser = () => {
      const ua = navigator.userAgent;

      if (/CriOS/i.test(ua)) {
        return "chrome-ios";
      }

      if (/FxiOS/i.test(ua)) {
        return "firefox-ios";
      }

      if (/EdgiOS/i.test(ua)) {
        return "edge-ios";
      }

      if (/OPiOS/i.test(ua)) {
        return "opera-ios";
      }

      if (/SamsungBrowser/i.test(ua)) {
        return "samsung";
      }

      if (/Edg/i.test(ua)) {
        return "edge";
      }

      if (/OPR|Opera/i.test(ua)) {
        return "opera";
      }

      if (/Chrome/i.test(ua) && !/Edge|Edg/i.test(ua)) {
        return "chrome";
      }

      if (/Firefox/i.test(ua)) {
        return "firefox";
      }

      if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) {
        return "safari";
      }

      return "other";
    };


    /* -------------------------------------------------------
       Check whether user recently dismissed popup
    ------------------------------------------------------- */

    const wasRecentlyDismissed = () => {
      try {
        const value = localStorage.getItem(STORAGE_KEY);

        if (!value) {
          return false;
        }

        const timestamp = Number(value);

        if (!timestamp) {
          return false;
        }

        const daysPassed =
          (Date.now() - timestamp) / (1000 * 60 * 60 * 24);

        if (daysPassed < DISMISS_DAYS) {
          return true;
        }

        localStorage.removeItem(STORAGE_KEY);
        return false;
      } catch (error) {
        return false;
      }
    };


    /* -------------------------------------------------------
       Remember dismissal
    ------------------------------------------------------- */

    const rememberDismissal = () => {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          String(Date.now())
        );
      } catch (error) {
        console.warn(
          "Could not save install popup preference:",
          error
        );
      }
    };


    /* -------------------------------------------------------
       Remove popup
    ------------------------------------------------------- */

    const removePopup = () => {
      if (popupElement) {
        popupElement.remove();
        popupElement = null;
      }
    };


    /* -------------------------------------------------------
       iPhone / iPad instructions
    ------------------------------------------------------- */

    const getIOSInstructions = () => {
      const browser = getBrowser();

      if (
        browser === "chrome-ios" ||
        browser === "edge-ios" ||
        browser === "firefox-ios" ||
        browser === "opera-ios"
      ) {
        return `
          <div style="
            margin-top:12px;
            background:#f5f8ff;
            border-radius:12px;
            padding:12px;
            text-align:left;
            color:#333;
            font-size:14px;
            line-height:1.55;
          ">
            <div style="font-weight:700;margin-bottom:6px;">
              📱 Install on iPhone / iPad
            </div>

            <div>
              1. Open this website in <b>Safari</b>
            </div>

            <div>
              2. Tap the <b>Share</b> button
            </div>

            <div>
              3. Select <b>Add to Home Screen</b>
            </div>

            <div>
              4. Tap <b>Add</b>
            </div>
          </div>
        `;
      }

      return `
        <div style="
          margin-top:12px;
          background:#f5f8ff;
          border-radius:12px;
          padding:12px;
          text-align:left;
          color:#333;
          font-size:14px;
          line-height:1.55;
        ">
          <div style="font-weight:700;margin-bottom:6px;">
            📱 Install Eyal App
          </div>

          <div>
            1. Tap the <b>Share</b> button
          </div>

          <div>
            2. Select <b>Add to Home Screen</b>
          </div>

          <div>
            3. Tap <b>Add</b>
          </div>
        </div>
      `;
    };


    /* -------------------------------------------------------
       Android manual installation instructions
    ------------------------------------------------------- */

    const getAndroidInstructions = () => {
      const browser = getBrowser();

      let browserName = "your browser";

      if (browser === "chrome") {
        browserName = "Chrome";
      } else if (browser === "edge") {
        browserName = "Edge";
      } else if (browser === "samsung") {
        browserName = "Samsung Internet";
      } else if (browser === "opera") {
        browserName = "Opera";
      }

      return `
        <div style="
          margin-top:12px;
          background:#f5f8ff;
          border-radius:12px;
          padding:12px;
          text-align:left;
          color:#333;
          font-size:14px;
          line-height:1.55;
        ">
          <div style="font-weight:700;margin-bottom:6px;">
            📱 Install Eyal App
          </div>

          <div>
            1. Tap your browser's <b>menu ⋮</b>
          </div>

          <div>
            2. Select <b>Install app</b> or
            <b>Add to Home screen</b>
          </div>

          <div>
            3. Confirm the installation
          </div>

          <div style="
            margin-top:7px;
            color:#666;
            font-size:12px;
          ">
            Using ${browserName}
          </div>
        </div>
      `;
    };


    /* -------------------------------------------------------
       Show custom popup
    ------------------------------------------------------- */

    const showInstallPopup = (mode = "native") => {
      if (popupElement) {
        return;
      }

      if (isStandalone()) {
        return;
      }

      popupElement = document.createElement("div");

      popupElement.style.cssText = `
        position: fixed;
        left: 50%;
        bottom: calc(20px + env(safe-area-inset-bottom));
        transform: translateX(-50%);
        width: calc(100% - 28px);
        max-width: 390px;
        background: #ffffff;
        border-radius: 22px;
        padding: 20px;
        box-shadow:
          0 12px 40px rgba(0,0,0,0.20),
          0 3px 10px rgba(0,0,0,0.08);
        z-index: 2147483647;
        text-align: center;
        font-family:
          -apple-system,
          BlinkMacSystemFont,
          "Segoe UI",
          Roboto,
          Arial,
          sans-serif;
        box-sizing: border-box;
        animation: eyalInstallPopupIn 0.28s ease-out;
      `;

      const browser = getBrowser();

      let title = "Install Eyal App";
      let message =
        "Get faster access to Eyal App from your home screen. 🚀";

      let buttonText = "Install App";
      let instructions = "";

      if (mode === "ios") {
        title = "Install Eyal App 📲";
        message =
          "Add Eyal App to your iPhone or iPad home screen for quick access.";
        buttonText = "How to Install";
        instructions = getIOSInstructions();
      } else if (mode === "manual-android") {
        title = "Install Eyal App 📲";
        message =
          "Add Eyal App to your home screen for a faster app-like experience.";
        buttonText = "Show Instructions";
        instructions = getAndroidInstructions();
      }

      popupElement.innerHTML = `
        <style>
          @keyframes eyalInstallPopupIn {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }

          #eyalInstallMainBtn:hover {
            opacity: 0.92;
          }

          #eyalInstallCloseBtn:hover {
            background: #eeeeee;
          }
        </style>

        <div style="
          display:flex;
          align-items:center;
          justify-content:space-between;
          margin-bottom:12px;
        ">

          <div style="
            display:flex;
            align-items:center;
            gap:10px;
            text-align:left;
          ">

            <img
              src="/images/icons/192x192.png"
              alt="Eyal Mart"
              style="
                width:48px;
                height:48px;
                border-radius:12px;
                object-fit:cover;
                flex-shrink:0;
              "
              onerror="
                this.style.display='none';
              "
            />

            <div>
              <div style="
                font-size:18px;
                font-weight:750;
                color:#111827;
                line-height:1.2;
              ">
                ${title}
              </div>

              <div style="
                font-size:12px;
                color:#6b7280;
                margin-top:3px;
              ">
                Eyal Mart
              </div>
            </div>
          </div>

          <button
            id="eyalInstallCloseBtn"
            aria-label="Close"
            style="
              width:32px;
              height:32px;
              border:none;
              border-radius:50%;
              background:#f5f5f5;
              color:#555;
              font-size:20px;
              line-height:32px;
              cursor:pointer;
              padding:0;
            "
          >
            ×
          </button>
        </div>

        <div style="
          font-size:14px;
          line-height:1.5;
          color:#4b5563;
          margin-bottom:15px;
        ">
          ${message}
        </div>

        <div id="eyalInstallInstructions">
          ${instructions}
        </div>

        <div style="
          display:flex;
          gap:10px;
          margin-top:16px;
        ">

          <button
            id="eyalInstallMainBtn"
            style="
              flex:1;
              background:#0061ff;
              color:white;
              border:none;
              border-radius:12px;
              padding:12px 14px;
              font-size:15px;
              font-weight:700;
              cursor:pointer;
              min-height:46px;
            "
          >
            ${buttonText}
          </button>

          <button
            id="eyalInstallLaterBtn"
            style="
              flex:1;
              background:#f1f3f5;
              color:#374151;
              border:none;
              border-radius:12px;
              padding:12px 14px;
              font-size:14px;
              font-weight:600;
              cursor:pointer;
              min-height:46px;
            "
          >
            Later
          </button>

        </div>
      `;

      document.body.appendChild(popupElement);

      const mainButton =
        popupElement.querySelector("#eyalInstallMainBtn");

      const laterButton =
        popupElement.querySelector("#eyalInstallLaterBtn");

      const closeButton =
        popupElement.querySelector("#eyalInstallCloseBtn");

      /* -----------------------------------------------------
         Close
      ----------------------------------------------------- */

      const closePopup = () => {
        rememberDismissal();
        removePopup();
      };

      closeButton.onclick = closePopup;
      laterButton.onclick = closePopup;


      /* -----------------------------------------------------
         Native Android / Chrome installation
      ----------------------------------------------------- */

      if (mode === "native") {
        mainButton.onclick = async () => {
          if (!deferredPrompt) {
            /*
             * The browser did not provide the native
             * installation API.
             *
             * Show manual instructions instead.
             */

            if (isAndroid()) {
              const instructionsBox =
                popupElement.querySelector(
                  "#eyalInstallInstructions"
                );

              instructionsBox.innerHTML =
                getAndroidInstructions();

              mainButton.textContent = "Got It";

              mainButton.onclick = () => {
                removePopup();
              };

              return;
            }

            removePopup();
            return;
          }

          try {
            deferredPrompt.prompt();

            const choiceResult =
              await deferredPrompt.userChoice;

            console.log(
              "PWA install result:",
              choiceResult?.outcome
            );

            deferredPrompt = null;

            removePopup();
          } catch (error) {
            console.error(
              "PWA installation failed:",
              error
            );

            deferredPrompt = null;

            removePopup();
          }
        };
      }


      /* -----------------------------------------------------
         iPhone / iPad
      ----------------------------------------------------- */

      if (mode === "ios") {
        mainButton.onclick = () => {
          const instructionsBox =
            popupElement.querySelector(
              "#eyalInstallInstructions"
            );

          instructionsBox.innerHTML =
            getIOSInstructions();

          mainButton.textContent = "Got It";

          mainButton.onclick = () => {
            removePopup();
          };
        };
      }


      /* -----------------------------------------------------
         Android manual installation
      ----------------------------------------------------- */

      if (mode === "manual-android") {
        mainButton.onclick = () => {
          const instructionsBox =
            popupElement.querySelector(
              "#eyalInstallInstructions"
            );

          instructionsBox.innerHTML =
            getAndroidInstructions();

          mainButton.textContent = "Got It";

          mainButton.onclick = () => {
            removePopup();
          };
        };
      }
    };


    /* =======================================================
       BEFORE INSTALL PROMPT
       Chrome / Edge / Samsung Internet / supported browsers
    ======================================================= */

    const handleBeforeInstallPrompt = (event) => {
      console.log(
        "✅ beforeinstallprompt detected"
      );

      event.preventDefault();

      deferredPrompt = event;

      if (isStandalone()) {
        return;
      }

      if (wasRecentlyDismissed()) {
        return;
      }

      /*
       * Native-capable browser.
       */
      showInstallPopup("native");
    };


    window.addEventListener(
      "beforeinstallprompt",
      handleBeforeInstallPrompt
    );


    /* =======================================================
       APP INSTALLED
    ======================================================= */

    const handleAppInstalled = () => {
      console.log(
        "✅ Eyal App PWA installed"
      );

      deferredPrompt = null;

      removePopup();

      try {
        localStorage.removeItem(
          STORAGE_KEY
        );
      } catch (error) {
        // Ignore storage errors
      }
    };


    window.addEventListener(
      "appinstalled",
      handleAppInstalled
    );


    /* =======================================================
       INITIAL CHECK
    ======================================================= */

    const checkInstallation = () => {
      if (isStandalone()) {
        console.log(
          "✅ Eyal App is already installed"
        );

        return;
      }

      if (wasRecentlyDismissed()) {
        return;
      }

      /*
       * Give the browser time to evaluate the manifest
       * and service worker.
       */
      setTimeout(() => {

        if (isStandalone()) {
          return;
        }

        /*
         * iPhone / iPad
         *
         * There is no beforeinstallprompt API.
         * Therefore show our custom Eyal popup.
         */
        if (isIOS()) {
          console.log(
            "🍎 iOS detected"
          );

          showInstallPopup("ios");

          return;
        }


        /*
         * Android
         *
         * If beforeinstallprompt already fired,
         * the native popup is already being shown.
         *
         * Otherwise provide our manual installation
         * instructions.
         */
        if (isAndroid()) {
          if (deferredPrompt) {
            return;
          }

          console.log(
            "🤖 Android detected - native prompt unavailable"
          );

          showInstallPopup(
            "manual-android"
          );

          return;
        }


        /*
         * Desktop
         *
         * If native prompt wasn't available,
         * show a general install popup.
         */
        if (
          !deferredPrompt &&
          (getBrowser() === "chrome" ||
            getBrowser() === "edge" ||
            getBrowser() === "opera")
        ) {
          showInstallPopup(
            "manual-android"
          );
        }

      }, 2500);
    };


    /*
     * Start after page load.
     */
    if (document.readyState === "complete") {
      checkInstallation();
    } else {
      window.addEventListener(
        "load",
        checkInstallation,
        { once: true }
      );
    }


    /* =======================================================
       CLEANUP
    ======================================================= */

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );

      window.removeEventListener(
        "appinstalled",
        handleAppInstalled
      );

      window.removeEventListener(
        "load",
        checkInstallation
      );

      removePopup();
    };

  }, []);
}


/* =========================================================
   NotificationInitializer
========================================================= */

function NotificationInitializer() {
  const { user } = useAuth();

  useEffect(() => {
  if (!user) return;

  let unsubscribe = null;

  const setupNotifications = async () => {
    try {
      await enableNotifications();

      unsubscribe =
        listenNotifications();

    } catch (error) {
      console.error(
        "❌ Notification setup failed:",
        error
      );
    }
  };

  setupNotifications();

  return () => {
    if (typeof unsubscribe === "function") {
      unsubscribe();
    }
  };
}, [user]);

  return null;
}


/* =========================================================
   App Component
========================================================= */

export default function App() {
  usePWAInstallPrompt();

  return (
    <AuthProvider>

      <NotificationInitializer />

      <CartProvider>

        <Router>

          <ReferralTracker />

          <TopNav />

          <div
            className="app-wrap"
            style={{
              paddingTop: "0px",
              paddingBottom: "70px",
              minHeight: "100vh",
              background: "#f9fafc",
            }}
          >

            <Routes>

              {/* =================================================
                  ROOT
              ================================================= */}

              <Route
                path="/"
                element={<Home />}
              />


              {/* =================================================
                  PUBLIC
              ================================================= */}

              <Route
                path="/home"
                element={<Home />}
              />

              <Route
                path="/foryou"
                element={<ForYou />}
              />

              <Route
                path="/posts"
                element={<Posts />}
              />

              <Route
                path="/videos"
                element={<Videos />}
              />

              <Route
                path="/add-video"
                element={<AddVideo />}
              />

              <Route
                path="/p"
                element={<PostViewer />}
              />

              <Route
                path="/postviewer"
                element={<PostViewer />}
              />

              <Route
                path="/postviewer/:id"
                element={<PostViewer />}
              />

              <Route
                path="/login"
                element={<Login />}
              />

              <Route
                path="/join"
                element={<Join />}
              />


              {/* =================================================
                  SHOPS
              ================================================= */}

              <Route
                path="/shops"
                element={<Shops />}
              />


              {/* =================================================
                  PRODUCTS
              ================================================= */}

              <Route
                path="/products"
                element={<Products />}
              />

              <Route
                path="/allproducts"
                element={<AllProducts />}
              />

              <Route
                path="/product/:id"
                element={<ProductDetail />}
              />

              <Route
                path="/cart"
                element={<Cart />}
              />

              <Route
                path="/deals"
                element={<Deals />}
              />

              <Route
                path="/checkout"
                element={<Checkout />}
              />

              <Route
                path="/success"
                element={<Success />}
              />

              <Route
                path="/orders"
                element={<MyOrders />}
              />

              <Route
                path="/bills"
                element={<Bills />}
              />

              <Route
                path="/shopproducts"
                element={<ShopProducts />}
              />


              {/* =================================================
                  AUTO
              ================================================= */}

              <Route
                path="/autos"
                element={<Autos />}
              />

              <Route
                path="/join-auto"
                element={
                  <PrivateRoute>
                    <JoinAuto />
                  </PrivateRoute>
                }
              />

              <Route
                path="/auto-dashboard"
                element={
                  <PrivateRoute>
                    <AutoDashboard />
                  </PrivateRoute>
                }
              />

              <Route
                path="/ride-requests"
                element={
                  <PrivateRoute>
                    <RideRequests />
                  </PrivateRoute>
                }
              />


              {/* =================================================
                  DELIVERY
              ================================================= */}

              <Route
                path="/join-delivery"
                element={
                  <PrivateRoute>
                    <JoinDeliveryPerson />
                  </PrivateRoute>
                }
              />

              <Route
                path="/delivery-dashboard"
                element={
                  <PrivateRoute>
                    <DeliveryDashboard />
                  </PrivateRoute>
                }
              />

              {/* =================================================
                  payments
              ================================================= */}

<Route
  path="/payments"
  element={
    <PrivateRoute>
      <Payments />
    </PrivateRoute>
  }
/>

{/* =================================================
                  ePOS
================================================= */}

<Route path="/ePOS" element={<EPOS />} />


              {/* =================================================
                  MORE
              ================================================= */}

              <Route
                path="/more"
                element={<More />}
              />


              {/* =================================================
                  JOBS
              ================================================= */}

              <Route
                path="/jobs"
                element={<Jobs />}
              />


              {/* =================================================
                  CREATORS
              ================================================= */}

              <Route
                path="/creators"
                element={<Creators />}
              />


              {/* =================================================
                  INFO EXCHANGE
              ================================================= */}

              <Route
                path="/ie"
                element={<InfoExchange />}
              />


              {/* =================================================
                  LOCAL UPDATES
              ================================================= */}

              <Route
                path="/localupdates"
                element={<LocalUpdates />}
              />


              {/* =================================================
                  PLAY
              ================================================= */}

              <Route
                path="/play"
                element={<Play />}
              />


              {/* =================================================
                  AI SHOP
              ================================================= */}

              <Route
                path="/ai"
                element={<AIShop />}
              />


              <Route
                path="/post/:id"
                element={<PostViewer />}
              />

              <Route
                path="/product/:id"
                element={<ProductDetail />}
              />


              {/* =================================================
                  USER PROFILE
              ================================================= */}

              <Route
                path="/user/profile"
                element={
                  <PrivateRoute>
                    <UserProfile />
                  </PrivateRoute>
                }
              />

              <Route
                path="/user/settings"
                element={
                  <PrivateRoute>
                    <UserSettings />
                  </PrivateRoute>
                }
              />

              <Route
                path="/user/posts"
                element={
                  <PrivateRoute>
                    <UserPosts />
                  </PrivateRoute>
                }
              />

              <Route
                path="/user/videos"
                element={
                  <PrivateRoute>
                    <MyVideos />
                  </PrivateRoute>
                }
              />

              <Route
                path="/user/shop"
                element={
                  <PrivateRoute>
                    <UserShops />
                  </PrivateRoute>
                }
              />


              {/* =================================================
                  SHOP
              ================================================= */}

              <Route
                path="/shop/:shopId"
                element={<ShopDetail />}
              />

              <Route
                path="/shop/orders"
                element={
                  <PrivateRoute>
                    <ShopOrders />
                  </PrivateRoute>
                }
              />


              {/* =================================================
                  ADMIN
              ================================================= */}

              <Route
                path="/admin"
                element={
                  <PrivateRoute>
                    <Admin />
                  </PrivateRoute>
                }
              />


              {/* =================================================
                  WALLET
              ================================================= */}

              <Route
                path="/wallet"
                element={
                  <PrivateRoute>
                    <Wallet />
                  </PrivateRoute>
                }
              />

              <Route
                path="/user/bills"
                element={
                  <PrivateRoute>
                    <Bills />
                  </PrivateRoute>
                }
              />

              <Route
                path="/user/loans"
                element={
                  <PrivateRoute>
                    <Loans />
                  </PrivateRoute>
                }
              />


              {/* =================================================
                  SHOP MANAGEMENT
              ================================================= */}

              <Route
                path="/shop/manage/:shopId"
                element={
                  <PrivateRoute>
                    <ManageProducts />
                  </PrivateRoute>
                }
              />

              <Route
                path="/shop/manage/:shopId/add-product"
                element={
                  <PrivateRoute>
                    <AddProduct />
                  </PrivateRoute>
                }
              />

              <Route
                path="/shop/manage/:shopId/edit/:productId"
                element={
                  <PrivateRoute>
                    <EditProduct />
                  </PrivateRoute>
                }
              />


              {/* =================================================
                  RIDES
              ================================================= */}

              <Route
                path="/book-auto"
                element={
                  <PrivateRoute>
                    <BookRide />
                  </PrivateRoute>
                }
              />

              <Route
                path="/my-rides"
                element={
                  <PrivateRoute>
                    <MyRides />
                  </PrivateRoute>
                }
              />


              {/* =================================================
                  VOLUNTEERS
              ================================================= */}

              <Route
                path="/volunteers"
                element={
                  <PrivateRoute>
                    <Volunteers />
                  </PrivateRoute>
                }
              />


              {/* =================================================
                  INVITE
              ================================================= */}

              <Route
                path="/invite"
                element={
                  <PrivateRoute>
                    <Invite />
                  </PrivateRoute>
                }
              />


              {/* =================================================
                  BUSINESS VERIFICATION
              ================================================= */}

              <Route
                path="/business-verification/:submissionId"
                element={
                  <PrivateRoute>
                    <BusinessOTPVerification />
                  </PrivateRoute>
                }
              />


              {/* =================================================
                  FALLBACK
              ================================================= */}

              <Route
                path="*"
                element={<Home />}
              />

            </Routes>

          </div>

          <BottomNav />

          {/* <FloatingAI /> */}

        </Router>

      </CartProvider>

    </AuthProvider>
  );
}